package com.imobly.imobly.ui.screens.show.showtenant

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.House
import androidx.compose.material.icons.filled.Phone
import androidx.compose.material.icons.filled.RemoveCircle
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.compose.rememberNavController
import com.imobly.imobly.domain.Tenant
import com.imobly.imobly.ui.components.button.ButtonComp
import com.imobly.imobly.ui.components.input.InputDropdownComp
import com.imobly.imobly.ui.components.searchbar.SearchBarComp
import com.imobly.imobly.ui.components.title.TitleComp
import com.imobly.imobly.ui.components.topbar.TopBarComp
import com.imobly.imobly.ui.screens.show.showproperty.TextInfoComp
import com.imobly.imobly.ui.theme.colors.BackGroundColor
import com.imobly.imobly.ui.theme.colors.PrimaryColor
import com.imobly.imobly.ui.theme.colors.TitleColor
import com.imobly.imobly.ui.theme.fonts.montserratFont
import com.imobly.imobly.viewmodel.TenantViewModel
import org.jetbrains.compose.ui.tooling.preview.Preview
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource

@Composable
fun ShowTenantsScreen(tenantViewModel: TenantViewModel) {

    val tenants: MutableState<List<Tenant>> = remember { tenantViewModel.tenants}
    LaunchedEffect(Unit) {
        tenantViewModel.findAllAction()
    }

    Scaffold(
        topBar = {
            TopBarComp()
        },
        snackbarHost = {
            SnackbarHost(tenantViewModel.snackMessage.value)
        },
        contentWindowInsets = WindowInsets.systemBars,
        content = { paddingValues ->
            Column(
                modifier = Modifier
                    .background(BackGroundColor)
                    .padding(paddingValues)
                    .fillMaxSize()
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                ) {
                    TitleComp("Meus Locatários", {
                        tenantViewModel.goToHome()
                    })

                    Box(
                        modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
                    ) {
                        ButtonComp(
                            "Cadastrar locatário",
                            { Icon(Icons.Default.Add, "Sinal de soma") },
                            PrimaryColor,
                            { tenantViewModel.goToCreateTenant() })
                    }

                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        SearchBarComp(
                            "Buscar locatários por nome ou CPF",
                            tenantViewModel.searchText.value,
                            { tenantViewModel.changeSearchText(it) },
                            { tenantViewModel.searchAction() }
                        )
                    }

                    Box(
                        modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center
                    ) {
                        InputDropdownComp(
                            label = "Status",
                            options = tenantViewModel.activeOptions(),
                            selectedOption = tenantViewModel.selectedOption(),
                            onOptionSelected = { selectedOption ->
                                tenantViewModel.onOptionSelectedActive(selectedOption)
                            },
                            modifier = Modifier
                                .padding(10.dp)
                                .widthIn(max = 700.dp)
                        )
                    }

                    LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f),
                        verticalArrangement = Arrangement.spacedBy(20.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        items(tenants.value) { tenant ->
                            TenantCard(tenant, tenantViewModel)
                        }
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    val totalPages = tenantViewModel.totalPages.value
                    if (totalPages > 1) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            LazyRow(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                items(totalPages) { pageIndex ->
                                    val isSelected = tenantViewModel.currentPage.value == pageIndex
                                    Surface(
                                        onClick = { tenantViewModel.loadPage(pageIndex) },
                                        shape = RoundedCornerShape(50),
                                        color = if (isSelected) PrimaryColor else Color.White,
                                        shadowElevation = 2.dp,
                                        modifier = Modifier.size(40.dp),
                                        border = if (!isSelected) BorderStroke(1.dp, Color(0xFFCCCCCC)) else null
                                    ) {
                                        Box(contentAlignment = Alignment.Center) {
                                            Text(
                                                text = "${pageIndex + 1}",
                                                color = if (isSelected) Color.White else TitleColor,
                                                fontFamily = montserratFont(),
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 14.sp
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    )
}

@Composable
fun TenantCard(tenant: Tenant, tenantViewModel: TenantViewModel) {
    Card(
        modifier = Modifier
            .padding(10.dp)
            .widthIn(max = 700.dp)
            .fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 4.dp),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(
            modifier = Modifier
                .padding(16.dp)
        ) {
            KamelImage(
                resource = { asyncPainterResource(tenant.pathImage) },
                contentDescription = "Imagem do locatário ${tenant.firstName}",
                modifier = Modifier
                    .fillMaxSize()
                    .heightIn(max = 200.dp)
                    .clip(RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)),
                contentScale = ContentScale.Crop,
                onLoading = { progress -> CircularProgressIndicator({ progress }) },
                onFailure = { CircularProgressIndicator() }
            )

            Spacer(modifier = Modifier.width(16.dp))

                Column {
                    Text(
                        text = "${tenant.firstName}  ${tenant.lastName}",
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = Color.Black,
                        fontFamily = montserratFont()
                    )

                    Spacer(modifier = Modifier.height(4.dp))
                }

                Column {
                    Row {
                        TextInfoComp(
                            "Imóveis alugado: ${tenant.properties.joinToString(", ") { it.title }}",
                            { Icon(Icons.Default.House, "Casas alugadas") }
                        )
                    }

                    Row {
                        TextInfoComp(
                            tenant.telephones.telephone1,
                            { Icon(Icons.Default.Phone, "User telephone") }
                        )
                    }

                    Row {
                        TextInfoComp(
                            tenant.email,
                            { Icon(Icons.Default.Email, "User email") }
                        )
                    }

                    Row {
                        TextInfoComp(
                            if (tenant.isEnabled) "Ativo" else "Inativo",
                            {
                                if (tenant.isEnabled) {
                                    Icon(Icons.Default.CheckCircle, "Enabled")
                                } else {
                                    Icon(Icons.Default.RemoveCircle, "Disabled")
                                }
                            }
                        )
                    }
                }
            }

            Button(
                onClick = {
                    tenantViewModel.goToEditTenant(tenant)
                },
                modifier = Modifier.align(Alignment.CenterHorizontally),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFFF2603F)
                ),
                shape = RoundedCornerShape(12.dp)
            ) {
                Text(
                    text = "Ver Detalhes",
                    fontWeight = FontWeight.Bold,
                    fontSize = 14.sp,
                    fontFamily = montserratFont()
                )
            }
    }
}

@Preview
@Composable
fun ShowTenantsScreenPreview() {
    val navControllerFake = rememberNavController()
    ShowTenantsScreen(TenantViewModel(navControllerFake))
}